import { Button, Form, Alert, Dialog } from '@/ui'
import { FETCH_PAYROLL_PROFILE_KEY, putPayrollProfile } from '../payrollActions'
import { FormikProvider, useFormik } from 'formik'
import { selectPayrollProfile } from '../payroll.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { getFetchError, getIsFetching } from '@/reducers/fetch'
import {
  NEXT,
  OTHER,
  POLICY_OPTIONS,
} from '../payrollWcEnrollmentRequirement.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getFieldName } from '@/base'
import { YesNoOptions } from '@/features/taxes/taxConstants'

export const WorkersCompModal = ({
  open,
  close,
}: {
  open: boolean
  close: () => void
}) => {
  const dispatch = useAppDispatch()
  const payrollProfile = useReselector(selectPayrollProfile)
  const error = useReselector(getFetchError, FETCH_PAYROLL_PROFILE_KEY)
  const isSubmitting = useReselector(getIsFetching, FETCH_PAYROLL_PROFILE_KEY)
  const formik = useFormik({
    initialValues: {
      wcEnrolled: payrollProfile?.wcEnrolled,
      wcEnrollmentStartedAt: payrollProfile?.wcEnrollmentStartedAt,
      wcEnrollmentCompletedAt: payrollProfile?.wcEnrollmentCompletedAt,
      wcEnrollmentPolicyDetails: payrollProfile?.wcEnrollmentPolicyDetails,
      wcProvider: payrollProfile?.wcProvider !== NEXT ? OTHER : NEXT,
      wcProviderInput:
        payrollProfile?.wcProvider !== NEXT ? payrollProfile?.wcProvider : '',
    },
    onSubmit: async ({ wcEnrolled, wcProvider, wcProviderInput }) => {
      if (payrollProfile) {
        const res = await putPayrollProfile({
          id: payrollProfile.id,
          wcEnrolled,
          // If the WC provider is not next, we ask for free input
          wcProvider:
            wcProvider === NEXT
              ? wcProvider
              : wcProviderInput
                ? wcProviderInput
                : undefined, // Send undefined if the input is null or empty string
          // If user indicates to us that they've enrolled in WC, we set started and completed date time
          wcEnrollmentStartedAt: wcEnrolled ? new Date().toISOString() : null,
          wcEnrollmentCompletedAt: wcEnrolled ? new Date().toISOString() : null,
        })(dispatch)
        if (res) {
          close()
        }
      }
    },
  })
  const { values, submitForm } = formik
  return (
    <FormikProvider value={formik}>
      <Dialog open={open} onOpenChange={close} onClose={close}>
        <Dialog.Content className="max-w-md">
          <Dialog.Title>Update Your Workers Compensation Status</Dialog.Title>

          <div className="space-y-4">
            {error && <Alert variant="error">{error.message}</Alert>}

            <Form.Select
              label="Do you have an existing workers compensation policy?"
              placeholder="Select an option"
              name={getFieldName<typeof values>('wcEnrolled')}
              options={YesNoOptions}
              required
            />

            {values.wcEnrolled && (
              <>
                <Form.Select
                  name={getFieldName<typeof formik.values>('wcProvider')}
                  label="Select your policy provider"
                  placeholder="Select an option"
                  options={POLICY_OPTIONS}
                  required
                />

                {values.wcProvider !== NEXT && (
                  <Form.Input
                    name="wcProviderInput"
                    label="Let us know your workers compensation policy provider"
                    placeholder="Input your policy provider"
                  />
                )}
              </>
            )}
          </div>

          <Dialog.Footer className="pt-4">
            <Button onClick={close} variant="secondary">
              Cancel
            </Button>
            <Button onClick={submitForm} loading={isSubmitting}>
              Update
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </FormikProvider>
  )
}
